// Every move is plain data ({ type, player, ...params }) so it can be replayed for undo and sent to the server.
// Each definition:
//   validate(gameState, move) -> error string, or null when the move is legal. Must not change state.
//   apply(gameState, move)    -> mutates gameState. Only called after validate passes.
//   describe(gameState, move) -> text for the move log, computed before apply: what was done, then (on lines of its
//                                own, "\n") what it cost.
//   summarize(before, after, move) -> optional further lines for the description once the move is applied.
//   phase                     -> the phase the move belongs to; checked by the engine before validate.
//   politicalAction           -> true for the political action itself (refused once given up, see Rules.Validate).
//   irreversible              -> true when the move reveals hidden information, so undo cannot go back past it.
const { Phase, MoveTypes } = require('./MoveTypes');
const { CivilCardTypes, MilitaryCardTypes } = require('../Constants/Names/CardTypes');
const { ResolveEvent } = require('./EventEffects');
const { GetAllStats, PlayerChanges } = require('../Utilities/Stats');
const CardToData = require('../Utilities/Converters/CardToData');
const { CardRef } = require('../Utilities/CardRefs');
const TerritoryNames = require('../Constants/Names/TerritoryNames');
const { RunProductionPhase } = require('./Production');
const TechnologyTypes = require('../Constants/TechnologyTypes');
const { Age, AgeOrder } = require('../Constants/Constants');
const { GetTakeCardCost, ActionShortage, WonderStagesPerAction, MilitaryCardsOverLimit,
    GetPopulationCost, MoveChoice, GetBuildCost, GetUpgradeCost, GetDevelopCost, GetWonderStagesCost, PayCost, AdjustmentText,
    WithCardDiscount, PoolText, TakenThisTurn, RevolutionNeedsAllActions, GetGovernmentCost } = require('./Costs');
const ActionEffects = require('../Constants/Actions/ActionEffects');
const { ResolveDecision, NextDecisionFor, DecisionOrderProblem, SameValue, DecisionTypes, Back, DecisionSubject } = require('./Decisions');
const { GetCardRowRemovalCount } = require('../Utilities/Utilities');
const { TurnFlags, TriggerEvents, FireTriggers, LogTrigger, Rules, HasRuleException, CostKinds, PoolKinds } = require('../Effects/Effects');
const LeaderNames = require('../Constants/Names/LeaderNames');
const WonderNames = require('../Constants/Names/WonderNames');
const { LeaderDefinition, WonderDefinition } = require('../Constants/CardVersions');

const CARD_ROW_SIZE = 13;
const MAX_MILITARY_DRAW = 3;

const isMilitaryType = (type) => Object.values(TechnologyTypes.Military).includes(type);
const isUrbanType = (type) => Object.values(TechnologyTypes.Urban).includes(type);
const isSpecialType = (type) => Object.values(TechnologyTypes.Special).includes(type);

// Farms, mines, urban buildings and military units: the cards workers go on
const isBuildable = (tech) => tech.workerCost !== null && tech.workerCost !== undefined;

// A cost for the move log, e.g. "2 food", "1 food: −1 Moses" or "2 resources, 2 from Patriotism"
const costText = (cost, unit) => `${cost.total} ${unit}${cost.adjustments.length ? `: ${AdjustmentText(cost)}` : ''}`
    + (cost.pools.length ? `, ${PoolText(cost)}` : '');
// A cost as its own line of a move's description, e.g. "\nSpent 4 resources"
// The government the player has, as its card
const GovernmentRef = (player) => CardRef({ type: CivilCardTypes.Technology, name: player.government, age: player.governmentTechnology.age });
const spentLine = (cost, unit) => `\nSpent ${costText(cost, unit)}`;

// Military units use a military action, everything else a civil action
const spendActionFor = (player, techType) => {
    if (isMilitaryType(techType))
    {
        player.militaryActionsUsed++;
    } else {
        player.SpendCivilActions(1);
    }
};

// Cards that can be prepared as events in the political phase
const isEventCard = (card) => card.type === MilitaryCardTypes.Event || card.type === MilitaryCardTypes.Territory;
// What a player may prepare: an event or territory, or (Confucius) any military card
const isPreparable = (player, card) => isEventCard(card) || HasRuleException(player, Rules.PrepareAnyMilitaryCard);

// A tactic played into a player's own area moves to the common tactics (it stays their current tactic), and so do
// those kept there (Sun Tzu)
const makeTacticPublic = (gameState, player) => {
    if (player.exclusiveTactic !== null)
    {
        // (the card the player is on stays theirs: it is now among the common ones)
        player.currentTacticId = player.exclusiveTactic.id ?? player.currentTacticId;
        gameState.publicTactics.push(player.exclusiveTactic);
        player.exclusiveTactic = null;
    }
    gameState.publicTactics.push(...player.keptTactics);
    player.keptTactics = [];
};
// The player's tactic is being replaced: with Sun Tzu, the old one stays in their area, where no one (not even they)
// can copy it, until the first turn without him; otherwise it becomes common
const setTacticAside = (gameState, player) => {
    if (HasRuleException(player, Rules.KeepTacticPrivate))
    {
        if (player.exclusiveTactic !== null)
        {
            player.keptTactics.push(player.exclusiveTactic);
            player.exclusiveTactic = null;
        }
        return;
    }
    makeTacticPublic(gameState, player);
};

// The start of the player's action phase: what cards do then (e.g. Boudica)
const StartActionPhase = (gameState, player) => FireTriggers(player, TriggerEvents.ActionPhaseStart);

const firstRoundOnlyTakesCards = (gameState) =>
    gameState.round === 1 ? "Only taking cards from the card row is allowed in the first round" : null;

// Whether the game is waiting on the player: a decision of theirs is open, or it is their turn and no one else's
// decision holds it up
const HasPriority = (gameState, playerNumber) => {
    if (gameState.decisions.some(decision => decision.player === playerNumber))
    {
        return true;
    }
    const blocking = require('./Decisions').ActiveBlocking(gameState);
    return playerNumber === gameState.currentPlayer && !(blocking && blocking.player !== playerNumber);
};

// What an action card's own action is for (e.g. { tech } for building a library), for a civil action for some actions
// only (Gutenberg's; see TriggerPreview RestrictedContext), or null
const actionCardContext = (player, move) => move.inner
    ? require('./TriggerPreview').RestrictedContext[move.inner.type]?.(player, move.inner) ?? null : null;

// The usual last checks for an action: not in the first round, and an action left to spend. An action that comes with
// an action card (move.free, see PlayActionCard) skips them: the card already paid its civil action.
const actionCheck = (gameState, move, needsAction) => move.free ? null : (firstRoundOnlyTakesCards(gameState) ?? needsAction());

// Spends a civil or military action, unless the move comes with an action card
// context: what the action is for, e.g. { tech } (a civil action for that only may pay; see PlayerState.SpendCivilActions)
const spendAction = (player, move, military = false, context = null) => {
    if (move.free)
    {
        return;
    }
    if (military)
    {
        player.militaryActionsUsed++;
    } else {
        player.SpendCivilActions(1, context);
    }
};

// A wonder with every stage built is completed: the covering tokens go back to their banks (the Louvre keeps its own),
// and what it does once, now (e.g. the Age III wonders' scoring)
const CompleteWonder = (player) => {
    const wonder = player.wonderInProgress;
    if (!wonder || wonder.stagesBuilt < wonder.card.stageCosts.length)
    {
        return;
    }
    player.wonders.push(wonder.card);
    player.wonderInProgress = null;
    if (WonderDefinition(wonder.card)?.keepsTokens)
    {
        player.CardState(wonder.card.name).tokens = wonder.card.stageCosts.length;
    }
    LogTrigger(player, wonder.card.name, WonderDefinition(wonder.card)?.onComplete?.(player, wonder.card));
};

// The Olympic Games (Pierre de Coubertin): playing a war or aggression while they last costs 8 culture
const OlympicPenalty = (gameState, player) => {
    if (!gameState.olympics)
    {
        return;
    }
    const lost = Math.min(8, player.culture);
    player.culture -= lost;
    LogTrigger(player, 'Olympic Games', `-${lost} culture (a war or aggression during the Olympic Games)`);
};

// The Nobel Prize (Alfred Nobel by the culture track): 4 culture at the end of a turn in which the civilization developed
// at least 2 technologies
const NobelPrize = (gameState, player) => {
    const developed = player.turnFlags.filter(flag => flag === TurnFlags.Developed).length;
    if (!gameState.nobelPrize || developed < 2)
    {
        return;
    }
    player.culture += 4;
    LogTrigger(player, 'Nobel Prize', `+4 culture (${developed} technologies developed this turn)`);
};

// Saladin's choice after the action phase, by its value
const SaladinChoices = { strength: '+2 strength', civil: '+1 civil action' };

// Always the last check in a move's validate (see IsActionShortage)
const needsCivilActions = (player, count = 1, context = null) =>
    player.GetCivilActionsRemaining(context) < count ? ActionShortage("civil", count) : null;

const needsMilitaryActions = (player, count = 1) =>
    player.GetMilitaryActionsRemaining() < count ? ActionShortage("military", count) : null;

const MoveDefinitions = {

    // Taking no political action; moves on to the action phase
    [MoveTypes.PassPoliticalPhase]: {
        phase: Phase.Political,
        validate: () => null,
        apply: (gameState, move) => {
            // Not playing Julius Caesar's second political action keeps it for later
            const player = gameState.playerStates[move.player];
            player.turnFlags = player.turnFlags.filter(flag => flag !== TurnFlags.CaesarSecond);
            gameState.phase = Phase.Action;
            StartActionPhase(gameState, player);
        },
        describe: (gameState, move) => gameState.playerStates[move.player].turnFlags.includes(TurnFlags.CaesarSecond)
            ? (LeaderDefinition(gameState.playerStates[move.player].leader)?.oncePerGame
                ? "Ended the political phase (keeping Julius Caesar's second political action)"
                : "Ended the political phase (without Julius Caesar's second political action)")
            : "Passed political phase",
    },

    // { cardId }
    [MoveTypes.TakeCard]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const slot = gameState.civilCardRow.findIndex(card => card?.id === move.cardId);
            if (slot === -1)
            {
                return "That card is not in the card row";
            }
            const card = gameState.civilCardRow[slot];
            if (card.type === CivilCardTypes.Wonder)
            {
                // Wonders go straight into play, so they do not count against the hand limit
                if (player.wonderInProgress !== null)
                {
                    return "You can only have one unfinished wonder";
                }
            } else if (player.civilHand.length >= player.GetCivilHandLimit())
            {
                return "Your civil hand is full";
            }
            if (card.type === CivilCardTypes.Technology
                && (player.HasTechnology(card.name) || player.civilHand.some(c => c.name === card.name)))
            {
                return "You already have this technology";
            }
            if (card.type === CivilCardTypes.Leader && player.leaderAgesTaken.includes(card.age))
            {
                return "You have already taken a leader from this age";
            }
            return move.free ? null : needsCivilActions(player, GetTakeCardCost(player, card, slot), { card });
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const slot = gameState.civilCardRow.findIndex(card => card?.id === move.cardId);
            const card = gameState.civilCardRow[slot];
            if (!move.free)
            {
                player.SpendCivilActions(GetTakeCardCost(player, card, slot), { card });
            }
            gameState.civilCardRow[slot] = null;
            if (card.type === CivilCardTypes.Wonder)
            {
                player.wonderInProgress = { card, stagesBuilt: 0 };
            } else {
                player.civilHand.push(card);
                if (card.type === CivilCardTypes.Leader)
                {
                    player.leaderAgesTaken.push(card.age);
                }
            }
            player.takenThisTurn.push(card.id);
            FireTriggers(player, TriggerEvents.TakeCard, { card });
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const slot = gameState.civilCardRow.findIndex(card => card?.id === move.cardId);
            const card = gameState.civilCardRow[slot];
            const actions = GetTakeCardCost(player, card, slot);
            return `Took ${card ? CardRef(card) : 'a card'}\nUsed ${actions} civil action${actions === 1 ? '' : 's'}`;
        },
    },

    [MoveTypes.IncreasePopulation]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const cost = GetPopulationCost(player, MoveChoice(move));
            if (cost === null)
            {
                return "Your yellow bank is empty";
            }
            if (player.GetFood() < cost.pay)
            {
                return `Needs ${cost.pay} food`;
            }
            return actionCheck(gameState, move, () => needsCivilActions(player));
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const cost = GetPopulationCost(player, MoveChoice(move));
            player.PayFood(cost.pay);
            PayCost(player, cost);
            player.workerPool++;
            spendAction(player, move);
            FireTriggers(player, TriggerEvents.PopulationIncrease, { free: false });
        },
        describe: (gameState, move) =>
            `Increased population${spentLine(GetPopulationCost(gameState.playerStates[move.player], MoveChoice(move)), 'food')}`,
    },

    // { techName }: puts a worker from the pool onto a farm, mine, urban building or military unit
    [MoveTypes.Build]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const tech = player.GetTechnology(move.techName);
            if (tech === undefined)
            {
                return "You do not have that technology";
            }
            if (tech.workerCost === null || tech.workerCost === undefined)
            {
                return "Nothing can be built on that card";
            }
            if (player.workerPool < 1)
            {
                return "You have no unused workers";
            }
            const cost = GetBuildCost(player, tech, MoveChoice(move)).pay;
            if (player.GetResources() < cost)
            {
                return `Needs ${cost} resources`;
            }
            if (isUrbanType(tech.type))
            {
                const built = player.technologies
                    .filter(t => t.type === tech.type)
                    .reduce((sum, t) => sum + t.GetWorkerCount(), 0);
                if (built >= player.GetUrbanBuildingLimit())
                {
                    return `Your government allows only ${player.GetUrbanBuildingLimit()} ${tech.type} buildings`;
                }
            }
            return actionCheck(gameState, move,
                () => isMilitaryType(tech.type) ? needsMilitaryActions(player) : needsCivilActions(player, 1, { tech }));
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const tech = player.GetTechnology(move.techName);
            const cost = GetBuildCost(player, tech, MoveChoice(move));
            player.PayResources(cost.pay);
            PayCost(player, cost);
            player.workerPool--;
            tech.AddContent("workers");
            spendAction(player, move, isMilitaryType(tech.type), { tech });
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const tech = player.GetTechnology(move.techName);
            return tech ? `Built ${move.techName}${spentLine(GetBuildCost(player, tech, MoveChoice(move)), 'resources')}` : `Built ${move.techName}`;
        },
    },

    // { cardId }: plays a technology card from the civil hand
    // { fromTech, toTech }: moves a worker to a higher-level card of the same type, paying the difference in cost
    [MoveTypes.Upgrade]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const from = player.GetTechnology(move.fromTech);
            const to = player.GetTechnology(move.toTech);
            if (from === undefined || to === undefined)
            {
                return "You do not have that technology";
            }
            if (from.type !== to.type || !isBuildable(from) || AgeOrder.indexOf(to.age) <= AgeOrder.indexOf(from.age))
            {
                return "Can only upgrade to a higher level of the same kind of card";
            }
            if (from.GetWorkerCount() < 1)
            {
                return `Nothing is built on ${from.name}`;
            }
            const cost = GetUpgradeCost(player, from, to, MoveChoice(move)).pay;
            if (player.GetResources() < cost)
            {
                return `Needs ${cost} resources`;
            }
            return actionCheck(gameState, move,
                () => isMilitaryType(from.type) ? needsMilitaryActions(player) : needsCivilActions(player, 1, { tech: to }));
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const from = player.GetTechnology(move.fromTech);
            const to = player.GetTechnology(move.toTech);
            const cost = GetUpgradeCost(player, from, to, MoveChoice(move));
            player.PayResources(cost.pay);
            PayCost(player, cost);
            from.AddContent("workers", -1);
            to.AddContent("workers");
            spendAction(player, move, isMilitaryType(from.type), { tech: to });
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const cost = GetUpgradeCost(player, player.GetTechnology(move.fromTech), player.GetTechnology(move.toTech), MoveChoice(move));
            return `Upgraded ${move.fromTech} to ${move.toTech}${spentLine(cost, 'resources')}`;
        },
    },

    // { techName }: returns a worker from a building or unit to the worker pool
    [MoveTypes.Destroy]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const tech = player.GetTechnology(move.techName);
            if (tech === undefined || !isBuildable(tech))
            {
                return "Nothing can be built on that card";
            }
            if (tech.GetWorkerCount() < 1)
            {
                return `Nothing is built on ${tech.name}`;
            }
            return firstRoundOnlyTakesCards(gameState)
                ?? (isMilitaryType(tech.type) ? needsMilitaryActions(player) : needsCivilActions(player));
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const tech = player.GetTechnology(move.techName);
            tech.AddContent("workers", -1);
            player.workerPool++;
            spendActionFor(player, tech.type);
        },
        describe: (gameState, move) => {
            const tech = gameState.playerStates[move.player].GetTechnology(move.techName);
            return isMilitaryType(tech?.type) ? `Disbanded a ${move.techName} unit` : `Destroyed a ${move.techName} building`;
        },
    },

    // { cardId, inner?, choice? }: plays a yellow action card from the civil hand for 1 civil action (see
    // Constants/Actions/ActionEffects.js). inner is the action that comes with the card, as a move without player
    // (e.g. { type: Build, techName }); it costs no civil action of its own, and the card's discount applies to it.
    [MoveTypes.PlayActionCard]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            if (card === undefined || card.type !== CivilCardTypes.Action)
            {
                return "That action card is not in your hand";
            }
            const effect = ActionEffects[card.name];
            if (effect === undefined)
            {
                return `${card.name} is not implemented yet`;
            }
            if (effect.choices && !effect.choices(card, player).some(choice => choice.value === move.choice))
            {
                return "Choose one of the card's options";
            }
            if (effect.inner)
            {
                const error = innerActionError(gameState, player, card, effect, move.inner, MoveChoice(move));
                if (error)
                {
                    return error;
                }
            }
            return firstRoundOnlyTakesCards(gameState)
                // (Harvard College: one taken this turn, once a turn)
                ?? (player.takenThisTurn.includes(card.id) && !(HasRuleException(player, Rules.PlayActionCardsTakenThisTurn) && !player.turnFlags.includes(TurnFlags.Harvard))
                    ? TakenThisTurn : null)
                // (a revolution with the card is paid for instead of its civil action: see RevolutionWithCard)
                ?? (RevolutionWithCard(move) ? null : needsCivilActions(player, 1, actionCardContext(player, move)));
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            const effect = ActionEffects[card.name];
            player.civilHand = player.civilHand.filter(c => c !== card);
            // (where this card's turn flag goes: before anything its action notes, e.g. Silk Road's first stage built with
            // Engineering Genius, after which the next card counts as the first)
            const flagsAt = player.turnFlags.length;
            // A civil action for some actions only (Gutenberg's) pays once the card's action is done, so its discount
            // applies to it too
            const context = actionCardContext(player, move);
            const restricted = context && player.RestrictedFor(context);
            if (!restricted && !RevolutionWithCard(move))
            {
                player.SpendCivilActions(1);
            }
            if (player.takenThisTurn.includes(card.id))
            {
                player.turnFlags.push(TurnFlags.Harvard);
            }
            require('./Aggression').KidnapBonus(player, card);
            if (effect.inner)
            {
                const inner = innerMove(move);
                WithCardDiscount(player, cardDiscount(card, effect, player), () => MoveDefinitions[inner.type].apply(gameState, inner));
                LogTrigger(player, card.name, effect.after?.(player, card, gameState));
            } else {
                LogTrigger(player, card.name, effect.play(player, card, move, gameState));
            }
            if (restricted)
            {
                player.SpendCivilActions(1, context);
            }
            // (Silk Road: its 1 more, for the first action card of the turn, is used up; noted when it applied)
            const silk = require('../Constants/Actions/ActionEffects').SilkRoadBonus(player);
            if (silk > 0)
            {
                LogTrigger(player, 'Silk Road', '+1 to its benefit (the first action card this turn)');
            }
            player.turnFlags.splice(Math.min(flagsAt, player.turnFlags.length), 0, TurnFlags.ActionCardPlayed);
        },
        // e.g. "Played Rich Land: Built Bronze (0 resources: −1 Rich Land)"; what the card itself does is added by
        // the trigger log, e.g. ". Stockpile: +1 food, +1 resource"
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            const effect = card && ActionEffects[card.name];
            if (!effect?.inner)
            {
                return `Played ${card ? CardRef(card) : 'a card'}`;
            }
            const inner = innerMove(move);
            return WithCardDiscount(player, cardDiscount(card, effect, player),
                () => `Played ${CardRef(card)}: ${MoveDefinitions[inner.type].describe(gameState, inner)}`);
        },
    },

    // Alexander the Great's political action: remove him from play to add 1 yellow token from the box to the yellow bank
    [MoveTypes.AlexanderRetire]: {
        phase: Phase.Political,
        politicalAction: true,
        validate: (gameState, move) => gameState.playerStates[move.player].leader?.name === LeaderNames.AlexanderTheGreat
            ? null : "Alexander the Great is not your leader",
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            FireTriggers(player, TriggerEvents.LeaderLeaves, { leader: player.leader });
            player.leader = null;
            player.ChangeTokens('yellow', 1, LeaderNames.AlexanderTheGreat);
            // That was the political action
            EndPoliticalAction(gameState, move.player);
        },
        describe: () => "Removed Alexander the Great from play for 1 yellow token",
    },

    // A stage of another player's International Red Cross (its owner: move.owner), once a turn, for its food and a civil
    // action: 6 culture to whoever builds it; its owner's blue token covers it, as for every stage
    [MoveTypes.RedCrossStage]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const owner = gameState.playerStates[move.owner];
            const wonder = owner?.wonderInProgress;
            if (!owner || owner === player || wonder?.card.name !== WonderNames.InternationalRedCross)
            {
                return "No International Red Cross of another player is being built";
            }
            if (player.turnFlags.includes(TurnFlags.RedCross))
            {
                return "You can build 1 stage of another player's Red Cross per turn";
            }
            const cost = wonder.card.stageCosts[wonder.stagesBuilt];
            if (player.GetFood() < cost)
            {
                return `Needs ${cost} food`;
            }
            return needsCivilActions(player);
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const owner = gameState.playerStates[move.owner];
            const wonder = owner.wonderInProgress;
            player.PayFood(wonder.card.stageCosts[wonder.stagesBuilt]);
            wonder.stagesBy = [...(wonder.stagesBy ?? Array(wonder.stagesBuilt).fill(owner.playerNumber)), player.playerNumber];
            wonder.stagesBuilt++;
            player.culture += 6;
            player.turnFlags.push(TurnFlags.RedCross);
            player.SpendCivilActions(1);
            LogTrigger(player, WonderNames.InternationalRedCross, '+6 culture');
            // Its owner's token covers the stage; with their bank empty, they choose where it comes from only once this
            // turn is over (not in the middle of it, which this player could still undo)
            if (owner.blueBank < 0 && !gameState.deferredBlue.includes(owner.playerNumber))
            {
                gameState.deferredBlue.push(owner.playerNumber);
            }
            CompleteWonder(owner);
        },
        describe: (gameState, move) => {
            const owner = gameState.playerStates[move.owner];
            const wonder = owner?.wonderInProgress;
            return `Built a stage of ${owner?.name}'s International Red Cross\nSpent ${wonder?.card.stageCosts[wonder.stagesBuilt]} food`;
        },
    },

    // The Louvre Museum: one of its blue tokens spent for 2 resources (no action), in the action phase
    // (at any time the player has priority: their own turn, or a decision the game waits on them for, e.g. a payment on
    // someone else's turn)
    [MoveTypes.LouvreSpend]: {
        anyPlayer: true,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            if (!player.wonders.some(w => w.name === WonderNames.LouvreMuseum && !w.crumbled) || !(player.CardState(WonderNames.LouvreMuseum).tokens >= 1))
            {
                return "No blue token left on the Louvre Museum";
            }
            return HasPriority(gameState, move.player) ? null : "Only when the game is waiting on you";
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            player.CardState(WonderNames.LouvreMuseum).tokens--;
            player.GainResources(2);
        },
        describe: () => "Spent a blue token from the Louvre Museum for 2 resources",
    },

    // Catherine the Great's political action, once per game: each civilization weaker than hers loses 1 yellow token
    // (from its yellow bank, if it has one); if at least one is lost (two in a 4-player game), she gains 1
    [MoveTypes.CatherineDecree]: {
        phase: Phase.Political,
        politicalAction: true,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            if (player.leader?.name !== LeaderNames.CatherineTheGreat)
            {
                return "Catherine the Great is not your leader";
            }
            return player.CardState(LeaderNames.CatherineTheGreat).used ? "Catherine the Great's political action is once per game" : null;
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const statsBefore = GetAllStats(gameState);
            const strength = player.GetMilitaryStrength();
            const weaker = gameState.playerStates.filter(other => other !== player && !other.resigned && other.GetMilitaryStrength() < strength);
            const losers = weaker.filter(other => other.yellowBank > 0);
            losers.forEach(other => other.ChangeTokens('yellow', -1, LeaderNames.CatherineTheGreat));
            const needed = gameState.playerCount >= 4 ? 2 : 1;
            const gains = losers.length >= needed;
            if (gains)
            {
                player.ChangeTokens('yellow', 1, LeaderNames.CatherineTheGreat);
            }
            player.CardState(LeaderNames.CatherineTheGreat).used = true;
            const lost = losers.length === 0 ? 'no one loses a yellow token' : `${losers.map(other => other.name).join(' and ')} lose${losers.length === 1 ? 's' : ''} 1 yellow token`;
            LogTrigger(player, LeaderNames.CatherineTheGreat, `${lost}${gains ? `; ${player.name} gains 1` : ''}`);
            // (each civilization's change is its own line; what did not happen and why, as notes)
            const notes = [
                ...weaker.filter(other => !losers.includes(other)).map(other => ({ player: other.name, text: 'loses nothing (no yellow token in the bank)' })),
                ...(!gains && losers.length > 0 ? [{ player: player.name, text: `gains nothing (${needed} tokens lost needed)` }] : []),
                ...(weaker.length === 0 ? [{ player: player.name, text: 'no civilization is weaker' }] : []),
            ];
            gameState.reports.push({ kind: 'leader', verb: 'activated', name: player.leader.name, age: player.leader.age, card: CardToData(player.leader), revealedBy: player.name, round: gameState.round,
                changes: PlayerChanges(statsBefore, GetAllStats(gameState)),
                ...(notes.length ? { choices: notes.map((note, index) => ({ decisionId: `catherine${index}`, type: 'catherine', ...note })) } : {}) });
            // That was the political action
            EndPoliticalAction(gameState, move.player);
        },
        describe: () => "Issued Catherine the Great's decree",
    },

    // Pierre de Coubertin, once per game, before the political action (it is not one): the Olympic Games, 8 culture, and
    // until the start of his player's next turn whoever plays a war or aggression loses 8 culture
    // (not a political action: with the political action given up for International Agreement, the political phase is
    // there for it all the same, see HasPrePoliticalAction)
    [MoveTypes.CoubertinOlympics]: {
        phase: Phase.Political,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            if (player.leader?.name !== LeaderNames.PierreDeCoubertin)
            {
                return "Pierre de Coubertin is not your leader";
            }
            return player.CardState(LeaderNames.PierreDeCoubertin).used ? "The Olympic Games are once per game" : null;
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            player.culture += 8;
            player.CardState(LeaderNames.PierreDeCoubertin).used = true;
            gameState.olympics = { player: player.playerNumber };
            gameState.reports.push({ kind: 'notice', name: 'Olympic Games', round: gameState.round, changes: [], journal: 'Game',
                text: `${player.name} declares the Olympic Games: until the start of ${player.name}'s next turn, whoever plays a war or aggression loses 8 culture.` });
        },
        describe: () => "Declared the Olympic Games (+8 culture)",
    },

    // { cardId }: Columbus's political action: remove him from play to colonize a territory from the military hand, with
    // no auction and no sacrifice. It can be undone (the territory was the player's own card), unless the territory draws
    // military cards (Strategic Territory). Reported for everyone's recap: "[player] colonizes [territory]" and what changed.
    [MoveTypes.ColumbusColonize]: {
        phase: Phase.Political,
        politicalAction: true,
        irreversible: (gameState, move) => gameState.playerStates[move.player].militaryHand.find(c => c?.id === move.cardId)?.name === TerritoryNames.StrategicTerritory,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            if (player.leader?.name !== LeaderNames.ChristopherColumbus)
            {
                return "Christopher Columbus is not your leader";
            }
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            return card?.type === MilitaryCardTypes.Territory ? null : "That territory is not in your hand";
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            player.militaryHand = player.militaryHand.filter(c => c !== card);
            FireTriggers(player, TriggerEvents.LeaderLeaves, { leader: player.leader });
            const columbus = player.leader;
            const statsBefore = GetAllStats(gameState);
            player.leader = null;
            LogTrigger(player, columbus.name, `colonizes ${card.name}: ${require('./Colonization').Colonize(gameState, player, card)}`);
            // "Dave sacrifices [Columbus] to colonize [territory]", then what it gave (counted on a rebuilt copy: the
            // colony's lasting bonuses, e.g. happy faces, only count once the state is read again)
            const after = GetAllStats(new (require('../entities/GameState'))(JSON.parse(JSON.stringify(gameState.toData()))));
            gameState.reports.push({ kind: 'leader', name: columbus.name, age: columbus.age, card: CardToData(columbus), revealedBy: player.name, round: gameState.round,
                headline: `sacrifices ${CardRef(columbus)} to colonize ${CardRef(card)}`,
                changes: PlayerChanges(statsBefore, after) });
            // That was the political action
            EndPoliticalAction(gameState, move.player);
        },
        describe: (gameState, move) => {
            const card = gameState.playerStates[move.player].militaryHand.find(c => c?.id === move.cardId);
            return `Removed Christopher Columbus from play to colonize ${card ? CardRef(card) : 'a territory'}`;
        },
    },

    // { cardId, target }: an aggression from the military hand against a weaker rival, as the political action (see
    // Aggression.js). It shows the card and the rival answers, so it cannot be undone.
    [MoveTypes.PlayAggression]: {
        phase: Phase.Political,
        politicalAction: true,
        irreversible: true,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            if (card?.type !== MilitaryCardTypes.Aggression)
            {
                return "That aggression is not in your hand";
            }
            return require('./Aggression').AggressionProblem(player, gameState.playerStates[move.target], card, move.himejiUnit);
        },
        apply: (gameState, move) => {
            const { AggressionCost, StartAggression } = require('./Aggression');
            const player = gameState.playerStates[move.player];
            const defender = gameState.playerStates[move.target];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            const statsBefore = GetAllStats(gameState);
            player.militaryHand = player.militaryHand.filter(c => c !== card);
            player.militaryActionsUsed += AggressionCost(card, defender);
            OlympicPenalty(gameState, player);
            // That was the political action
            EndPoliticalAction(gameState, move.player);
            // Pacts it breaks end before it is resolved (listed first under it in the recap)
            const ended = require('./Pact').EndPactsOnAttack(gameState, player, defender);
            StartAggression(gameState, player, defender, card, statsBefore, move.himejiUnit ?? null);
            gameState.aggression.lines = ended;
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            return `Played ${card ? CardRef(card) : 'an aggression'} against ${gameState.playerStates[move.target]?.name}`;
        },
    },

    // { cardId, target }: a war from the military hand against any rival, as the political action; it is resolved at
    // the start of the declarer's next turn (see War.js). It reveals nothing hidden (the card was the player's own), so
    // it can be undone until sent.
    [MoveTypes.DeclareWar]: {
        phase: Phase.Political,
        politicalAction: true,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            if (card?.type !== MilitaryCardTypes.War)
            {
                return "That war is not in your hand";
            }
            return require('./War').WarProblem(player, gameState.playerStates[move.target], card);
        },
        apply: (gameState, move) => {
            const { WarCost, DeclareWar } = require('./War');
            const player = gameState.playerStates[move.player];
            const defender = gameState.playerStates[move.target];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            player.militaryHand = player.militaryHand.filter(c => c !== card);
            player.militaryActionsUsed += WarCost(card, defender);
            OlympicPenalty(gameState, player);
            // That was the political action
            EndPoliticalAction(gameState, move.player);
            // Pacts it breaks end as it is declared
            DeclareWar(gameState, player, defender, card, require('./Pact').EndPactsOnAttack(gameState, player, defender));
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            return `Declared ${card ? CardRef(card) : 'a war'} on ${gameState.playerStates[move.target]?.name}`;
        },
    },

    // { cardId, target, offererIsA }: offers a pact from the military hand to a rival, as the political action (no
    // military actions); offererIsA picks the roles of a one-sided pact. The rival answers while the turn waits (see
    // Pact.js). It shows the card, so it cannot be undone.
    [MoveTypes.OfferPact]: {
        phase: Phase.Political,
        politicalAction: true,
        irreversible: true,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            if (card?.type !== MilitaryCardTypes.Pact)
            {
                return "That pact is not in your hand";
            }
            return require('./Pact').OfferProblem(gameState, player, gameState.playerStates[move.target]);
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            player.militaryHand = player.militaryHand.filter(c => c !== card);
            // That was the political action
            EndPoliticalAction(gameState, move.player);
            require('./Pact').OfferPact(gameState, player, gameState.playerStates[move.target], card, move.offererIsA !== false);
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            return `Offered ${card ? CardRef(card) : 'a pact'} to ${gameState.playerStates[move.target]?.name}`;
        },
    },

    // { cardId }: cancels a pact in play that the player is in (either side may), as the political action. It changes
    // the other side too; it can be undone, unless it leaves someone else a decision (e.g. blue tokens to give up), which
    // is theirs to answer now.
    [MoveTypes.CancelPact]: {
        phase: Phase.Political,
        politicalAction: true,
        irreversible: (gameState, move) => require('../Rules').Step(gameState, move).after.decisions.some(d => d.player !== move.player),
        validate: (gameState, move) => gameState.pacts.some(pact => pact.card.id === move.cardId && (pact.a === move.player || pact.b === move.player))
            ? null : "You are not in that pact",
        apply: (gameState, move) => {
            const pact = gameState.pacts.find(p => p.card.id === move.cardId);
            require('./Pact').CancelPact(gameState, gameState.playerStates[move.player], pact);
            EndPoliticalAction(gameState, move.player);
        },
        describe: (gameState, move) => {
            const pact = gameState.pacts.find(p => p.card.id === move.cardId);
            const other = pact && gameState.playerStates[pact.a === move.player ? pact.b : pact.a];
            return pact ? `Cancelled ${CardRef(pact.card)} with ${other.name}` : "Cancelled a pact";
        },
    },

    // Ocean Liner, once per turn: 1 population for no civil action or food
    [MoveTypes.OceanLinerPopulation]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            if (!player.wonders.some(wonder => wonder.name === WonderNames.OceanLiner))
            {
                return "You do not have the Ocean Liner";
            }
            if (player.turnFlags.includes(TurnFlags.OceanLiner))
            {
                return "The Ocean Liner has been used this turn";
            }
            if (player.yellowBank < 1)
            {
                return "Your yellow bank is empty";
            }
            return firstRoundOnlyTakesCards(gameState);
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            require('./Colonization').IncreasePopulationFree(gameState, player, 1);
            player.turnFlags.push(TurnFlags.OceanLiner);
        },
        describe: () => "Increased population with the Ocean Liner (free)",
    },

    // { techName }: Barbarossa: for 1 military action, increase population and build a military unit with the new
    // worker, each for 1 less (food, then resources)
    [MoveTypes.BarbarossaRecruit]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            if (player.leader?.name !== LeaderNames.FrederickBarbarossa)
            {
                return "Frederick Barbarossa is not your leader";
            }
            const tech = player.GetTechnology(move.techName);
            if (!tech || !isMilitaryType(tech.type) || !isBuildable(tech))
            {
                return "Only military units can be built this way";
            }
            const { population, unit } = barbarossaCosts(player, tech, MoveChoice(move));
            if (population === null)
            {
                return "Your yellow bank is empty";
            }
            if (player.GetFood() < population.total)
            {
                return `Needs ${population.total} food`;
            }
            if (player.GetResources() < unit.pay)
            {
                return `Needs ${unit.pay} resources`;
            }
            return firstRoundOnlyTakesCards(gameState) ?? needsMilitaryActions(player);
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const tech = player.GetTechnology(move.techName);
            const { population, unit } = barbarossaCosts(player, tech, MoveChoice(move));
            player.PayFood(population.total);
            PayCost(player, population);
            player.PayResources(unit.pay);
            PayCost(player, unit);
            // The new worker goes straight onto the unit
            tech.AddContent("workers");
            player.militaryActionsUsed++;
            FireTriggers(player, TriggerEvents.PopulationIncrease, { free: false });
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const tech = player.GetTechnology(move.techName);
            const { population, unit } = barbarossaCosts(player, tech, MoveChoice(move));
            return `Recruited ${move.techName} with Frederick Barbarossa`
                + `\nSpent ${costText(population, 'food')} for the population\nSpent ${costText(unit, 'resources')} for the unit`;
        },
    },

    // { fromTech, toTech }: Bach, once per turn: for a civil action, upgrade any urban building to a theater of the same
    // level or higher, for the usual cost (the difference in cost)
    [MoveTypes.BachUpgrade]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            if (player.leader?.name !== LeaderNames.JohannesSebastianBach)
            {
                return "Johannes Sebastian Bach is not your leader";
            }
            if (player.turnFlags.includes(TurnFlags.BachUpgrade))
            {
                return "Bach's upgrade has been used this turn";
            }
            const from = player.GetTechnology(move.fromTech);
            const to = player.GetTechnology(move.toTech);
            if (!from || !to || to.type !== TechnologyTypes.Urban.Theater || !isUrbanType(from.type) || from.type === to.type)
            {
                return "Bach upgrades another urban building to a theater";
            }
            if (from.GetWorkerCount() < 1)
            {
                return `Nothing is built on ${from.name}`;
            }
            if (AgeOrder.indexOf(to.age) < AgeOrder.indexOf(from.age))
            {
                return "The theater must be the same level or higher";
            }
            const theaters = player.technologies.filter(t => t.type === to.type).reduce((sum, t) => sum + t.GetWorkerCount(), 0);
            if (theaters >= player.GetUrbanBuildingLimit())
            {
                return `Your government allows only ${player.GetUrbanBuildingLimit()} ${to.type} buildings`;
            }
            const cost = GetUpgradeCost(player, from, to, MoveChoice(move)).pay;
            if (player.GetResources() < cost)
            {
                return `Needs ${cost} resources`;
            }
            return firstRoundOnlyTakesCards(gameState) ?? needsCivilActions(player);
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const from = player.GetTechnology(move.fromTech);
            const to = player.GetTechnology(move.toTech);
            const cost = GetUpgradeCost(player, from, to, MoveChoice(move));
            player.PayResources(cost.pay);
            PayCost(player, cost);
            from.AddContent("workers", -1);
            to.AddContent("workers");
            player.SpendCivilActions(1);
            player.turnFlags.push(TurnFlags.BachUpgrade);
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const cost = GetUpgradeCost(player, player.GetTechnology(move.fromTech), player.GetTechnology(move.toTech), MoveChoice(move));
            return `Upgraded ${move.fromTech} to ${move.toTech} with Bach${spentLine(cost, 'resources')}`;
        },
    },

    // Churchill, once per turn: 3 science and 3 resources for military units and their technologies this turn, instead
    // of his 3 culture at the end of the turn
    [MoveTypes.ChurchillTake]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            if (player.leader?.name !== LeaderNames.WinstonChurchill)
            {
                return "Winston Churchill is not your leader";
            }
            if (player.turnFlags.includes(TurnFlags.Churchill))
            {
                return "Churchill's science and resources have been taken this turn";
            }
            return firstRoundOnlyTakesCards(gameState);
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const source = player.leader.name;
            player.turnPools.push({ source, kind: PoolKinds.MilitaryResources, amount: 3 });
            player.turnPools.push({ source, kind: PoolKinds.MilitaryScience, amount: 3 });
            player.turnFlags.push(TurnFlags.Churchill);
        },
        describe: () => "Took Winston Churchill's 3 science and 3 resources for military units (instead of 3 culture)",
    },

    // { cardId, revolution }: changes government to a government card from the civil hand. Peacefully: its full science
    // cost and 1 civil action. By a revolution: its (lower) revolution cost and all civil actions, none of which may be
    // spent yet (with Robespierre: all military actions instead). The new government's actions count straight away, so
    // any extra ones it gives are still to use. Either way it is developing a technology (Newton and the like).
    [MoveTypes.ChangeGovernment]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            if (card === undefined || card.Technology?.type !== TechnologyTypes.Government)
            {
                return "That government is not in your hand";
            }
            if (player.government === card.name)
            {
                return `${card.name} is already your government`;
            }
            const cost = GetGovernmentCost(player, card.Technology, move.revolution).total;
            if (player.science < cost)
            {
                return `Needs ${cost} science`;
            }
            const cooperation = require('./Pact').CooperationProblem(gameState, player);
            if (cooperation)
            {
                return cooperation;
            }
            // Peacefully as part of something else (an event): no civil action of its own
            if (move.free && !move.revolution)
            {
                return null;
            }
            const firstRound = firstRoundOnlyTakesCards(gameState);
            if (firstRound)
            {
                return firstRound;
            }
            if (!move.revolution)
            {
                return needsCivilActions(player);
            }
            if (HasRuleException(player, Rules.RevolutionWithMilitaryActions))
            {
                // (a military action for this turn only, e.g. Patriotism's, is spent first and is not part of the total)
                return player.militaryActionsUsed > player.GetTemporaryMilitaryActions() ? RevolutionNeedsAllActions("military") : null;
            }
            return player.civilActionsUsed > 0 ? RevolutionNeedsAllActions("civil") : null;
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            const tech = card.Technology;
            const cost = GetGovernmentCost(player, tech, move.revolution);
            player.science -= cost.total;
            PayCost(player, cost);
            require('./Pact').PayCooperation(gameState, player);
            player.civilHand = player.civilHand.filter(c => c !== card);
            if (!move.revolution)
            {
                if (!move.free)
                {
                    player.SpendCivilActions(1);
                }
            } else if (HasRuleException(player, Rules.RevolutionWithMilitaryActions)) {
                // Every military action, the new government's too (see GameState: settled once it is in effect)
                // (this turn's own military actions, e.g. Patriotism's, are not part of the total: they stay as they were)
                player.turnFlags.push(`revolution:military:${player.GetMilitaryActionCount()}`);
                player.militaryActionsUsed = player.GetMilitaryActionCount() + Math.min(player.militaryActionsUsed, player.GetTemporaryMilitaryActions());
            } else {
                // Every civil action, the new government's too (see GameState: settled once it is in effect)
                player.turnFlags.push(`revolution:civil:${player.GetCivilActionCount()}`);
                player.civilActionsUsed = player.GetCivilActionCount();
            }
            player.government = tech.name;
            if (move.revolution)
            {
                FireTriggers(player, TriggerEvents.Revolution, { government: tech });
            }
            player.turnFlags.push(TurnFlags.Developed);
            FireTriggers(player, TriggerEvents.Develop, { tech });
            require('./Aggression').KidnapBonus(player, card);
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            if (!card)
            {
                return "Changed government";
            }
            const cost = spentLine(GetGovernmentCost(player, card.Technology, move.revolution), 'science');
            if (!move.revolution)
            {
                return `Changed government peacefully from ${GovernmentRef(player)} to ${CardRef(card)}${cost}`;
            }
            const actions = HasRuleException(player, Rules.RevolutionWithMilitaryActions) ? "all military actions" : "all civil actions";
            return `Revolution: ${GovernmentRef(player)} to ${CardRef(card)}${cost}\nUsed ${actions}`;
        },
    },

    // { cardId, payWithMilitary? }: puts a leader from the hand into play, replacing the current one. payWithMilitary:
    // replacing Hammurabi, pay with a military action (his ability) instead of a civil action
    [MoveTypes.PlayLeader]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            if (card === undefined || card.type !== CivilCardTypes.Leader)
            {
                return "That leader is not in your hand";
            }
            if (move.homerWonderId !== undefined && !(player.leader?.name === LeaderNames.Homer
                && player.wonders.some(wonder => wonder.id === move.homerWonderId)))
            {
                return "Only replacing Homer adds a happy face to one of your completed wonders";
            }
            if (move.payWithMilitary && !(player.leader?.name === LeaderNames.Hammurabi
                && !player.turnFlags.includes(TurnFlags.MilitaryAsCivil) && player.GetMilitaryActionsRemaining() >= 1))
            {
                return "Only Hammurabi's ability, once per turn, pays for a civil action with a military action";
            }
            return firstRoundOnlyTakesCards(gameState) ?? needsCivilActions(player);
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            player.civilHand = player.civilHand.filter(c => c !== card);
            // Replacing a leader discards the old one and gives the civil action back. Replacing Hammurabi, the player may
            // pay with a military action instead (his ability); the civil action back is then a rebate, so only one already
            // spent this turn comes back (as the first action of a turn, there is none).
            if (move.payWithMilitary)
            {
                player.militaryActionsUsed++;
                player.turnFlags.push(TurnFlags.MilitaryAsCivil);
                player.civilActionsUsed = Math.max(0, player.civilActionsUsed - 1);
            }
            if (player.leader === null)
            {
                player.SpendCivilActions(1);
            } else {
                player.turnFlags.push(TurnFlags.ReplacedLeader);
                FireTriggers(player, TriggerEvents.LeaderLeaves, { leader: player.leader, replaced: true });
            }
            // Replacing Homer, the player may give up the returned civil action to add a happy face to a wonder
            if (move.homerWonderId !== undefined)
            {
                player.SpendCivilActions(1);
                player.wonderHappyFaces.push({ wonderId: move.homerWonderId, source: player.leader.name });
            }
            player.leader = card;
            require('./Aggression').KidnapBonus(player, card);
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            if (!player.leader)
            {
                return `Elected ${card ? CardRef(card) : 'a leader'}`;
            }
            const wonder = player.wonders.find(w => w.id === move.homerWonderId);
            return `Elected ${card ? CardRef(card) : 'a leader'}, replacing ${CardRef(player.leader)}${move.payWithMilitary ? ' (paid with a military action)' : ''}`
                + (wonder ? ` and adding 1 happy face to ${CardRef(wonder)} (instead of the returned civil action)` : '');
        },
    },

    // { stages }: builds the next stage(s) of the unfinished wonder
    [MoveTypes.BuildWonderStage]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const wonder = player.wonderInProgress;
            if (wonder === null)
            {
                return "You have no unfinished wonder";
            }
            const remaining = wonder.card.stageCosts.length - wonder.stagesBuilt;
            if (!Number.isInteger(move.stages) || move.stages < 1 || move.stages > remaining)
            {
                return `${wonder.card.name} has ${remaining} stage${remaining === 1 ? "" : "s"} left`;
            }
            if (move.stages > WonderStagesPerAction(player))
            {
                return `You can build ${WonderStagesPerAction(player)} stage(s) of a wonder per civil action`;
            }
            const cost = GetWonderStagesCost(player, move.stages, MoveChoice(move)).pay;
            // (in food for the International Red Cross)
            const currency = require('./Costs').WonderCurrency(wonder);
            if ((currency === 'food' ? player.GetFood() : player.GetResources()) < cost)
            {
                return `Needs ${cost} ${currency}`;
            }
            return actionCheck(gameState, move, () => needsCivilActions(player));
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const wonder = player.wonderInProgress;
            const cost = GetWonderStagesCost(player, move.stages, MoveChoice(move));
            if (require('./Costs').WonderCurrency(wonder) === 'food')
            {
                player.PayFood(cost.pay);
            } else {
                player.PayResources(cost.pay);
            }
            PayCost(player, cost);
            // (who built each stage: their blue token covers it, see PlayerState.blueBank)
            wonder.stagesBy = [...(wonder.stagesBy ?? Array(wonder.stagesBuilt).fill(player.playerNumber)), ...Array(move.stages).fill(player.playerNumber)];
            // Each built stage is covered with a blue token from the blue bank (counted as in use while it is there)
            // With the bank empty, the player chooses the farm or mine the token comes from (see Decisions SettleBlueBank)
            // (what building these stages does while it is under way, e.g. Stonehenge's science for their costs)
            // (Silk Road's first stage: the first action card after it this turn gets its bonus)
            if (wonder.card.name === WonderNames.SilkRoad && wonder.stagesBuilt === 0)
            {
                player.turnFlags.push(TurnFlags.SilkRoadStarted);
            }
            const builtCosts = wonder.card.stageCosts.slice(wonder.stagesBuilt, wonder.stagesBuilt + move.stages);
            LogTrigger(player, wonder.card.name, WonderDefinition(wonder.card)?.onStages?.(player, wonder.card, builtCosts));
            wonder.stagesBuilt += move.stages;
            CompleteWonder(player);
            spendAction(player, move);
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const stages = move.stages === 1 ? "1 stage" : `${move.stages} stages`;
            return `Built ${stages} of ${player.wonderInProgress ? CardRef(player.wonderInProgress.card) : 'a wonder'}${spentLine(GetWonderStagesCost(player, move.stages, MoveChoice(move)), require('./Costs').WonderCurrency(player.wonderInProgress))}`;
        },
        summarize: (before, after, move) =>
            after.playerStates[move.player].wonderInProgress === null ? "Wonder completed" : "",
    },

    // { cardId }: puts an event or territory from the military hand on the future events deck, scores culture
    // for its level, and reveals and resolves the top current event.
    // TODO: the current events deck is hidden from every client, so in multiplayer this move has to go to the
    // server, which reveals the event; the client cannot resolve it locally.
    [MoveTypes.PrepareEvent]: {
        phase: Phase.Political,
        politicalAction: true,
        irreversible: true,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            if (card === undefined || !isPreparable(player, card))
            {
                return "That event is not in your hand";
            }
            return null;
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            player.militaryHand = player.militaryHand.filter(c => c !== card);
            gameState.futureEvents.unshift(card);
            gameState.eventOwners[card.id] = player.playerNumber;
            player.culture += AgeOrder.indexOf(card.age);
            // Cards that react to preparing an event (e.g. Confucius), noted with what preparing scored
            const logged = gameState.triggerLog.length;
            FireTriggers(player, TriggerEvents.PrepareEvent, { card });
            // (told with the preparing, not after the event: see summarize)
            const preparedNotes = gameState.triggerLog.splice(logged);
            gameState.preparedNotes = preparedNotes;

            // Reveal and resolve the top current event, recording what it did to each civilization
            const revealed = gameState.currentEvents.shift();
            if (revealed)
            {
                const before = GetAllStats(gameState);
                const text = ResolveEvent(gameState, revealed);
                const rankings = gameState.lastRankings ?? [];
                gameState.lastRankings = null;
                const choices = gameState.lastChoices ?? [];
                gameState.lastChoices = null;
                gameState.lastEvent = {
                    id: revealed.id,
                    name: revealed.name,
                    card: CardToData(revealed),
                    // What preparing the other card scored (kept apart from what the event did)
                    preparedCulture: AgeOrder.indexOf(card.age),
                    ...(preparedNotes.length ? { preparedNotes } : {}),
                    age: revealed.age,
                    revealedBy: player.name,
                    round: gameState.round,
                    text,
                    // What it compared, for everyone to check the math (e.g. Nostradamus's +3 when picking the weakest)
                    ...(rankings.length ? { rankings } : {}),
                    // Choices made for players with nothing to choose from (e.g. their only wonder crumbling)
                    ...(choices.length ? { choices } : {}),
                    // (from the state rebuilt from data, as after any move: cards' effects are wired only then, e.g. a
                    // wonder that crumbled at once)
                    changes: PlayerChanges(before, GetAllStats(new (require('../entities/GameState'))(JSON.parse(JSON.stringify(gameState.toData()))))),
                };
            } else {
                gameState.lastEvent = null;
            }

            if (gameState.currentEvents.length === 0)
            {
                // The future events become the new current events: shuffled, then sorted so earlier ages come first
                const shuffled = gameState.Shuffle(gameState.futureEvents);
                gameState.currentEvents = shuffled
                    .map((c, index) => ({ c, index }))
                    .sort((a, b) => AgeOrder.indexOf(a.c.age) - AgeOrder.indexOf(b.c.age) || a.index - b.index)
                    .map(entry => entry.c);
                gameState.futureEvents = [];
            }
            // A political action ends the political phase
            EndPoliticalAction(gameState, move.player);
        },
        // The prepared card stays secret; only its age is known
        describe: (gameState, move) => {
            const card = gameState.playerStates[move.player].militaryHand.find(c => c?.id === move.cardId);
            return `Prepared an ${card?.age} event\nScored ${AgeOrder.indexOf(card?.age)} culture`;
        },
        summarize: (before, after) => [
            ...(after.preparedNotes ?? []),
            after.lastEvent ? `Revealed ${CardRef(after.lastEvent.card ?? after.lastEvent)}: ${after.lastEvent.text}` : '',
            ...(after.lastEvent?.rankings ?? []).map(ranking => require('./EventEffects').RankingText(ranking)),
        ].filter(Boolean).join('\n'),
    },

    // { cardId }: plays a tactic from the military hand as this player's current tactic
    [MoveTypes.PlayTactic]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            if (card === undefined || card.type !== MilitaryCardTypes.Tactic)
            {
                return "That tactic is not in your hand";
            }
            if (player.changedTacticThisTurn)
            {
                return "You can only play or copy one tactic per turn";
            }
            return firstRoundOnlyTakesCards(gameState) ?? needsMilitaryActions(player);
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.militaryHand.find(c => c?.id === move.cardId);
            player.militaryHand = player.militaryHand.filter(c => c !== card);
            setTacticAside(gameState, player);
            player.exclusiveTactic = card;
            player.currentTactic = card.name;
            player.currentTacticId = card.id;
            player.changedTacticThisTurn = true;
            player.militaryActionsUsed++;
        },
        describe: (gameState, move) =>
            `Played the ${gameState.playerStates[move.player].militaryHand.find(c => c?.id === move.cardId)?.name} tactic`,
    },

    // { tacticName, tacticCardId? }: uses one of the common tactics for 2 military actions; a card that has gained strength
    // (Sun Tzu) is not like the others of its name, so it is named by id
    [MoveTypes.CopyTactic]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = CopiedTactic(gameState, move);
            if (!card)
            {
                return "That tactic is not available to copy";
            }
            const bonused = !!(gameState.tacticBonuses[card.id] || gameState.tacticBonuses[player.currentTacticId]);
            if (player.currentTactic === move.tacticName && (player.currentTacticId === card.id || !bonused))
            {
                return "That is already your tactic";
            }
            if (player.changedTacticThisTurn)
            {
                return "You can only play or copy one tactic per turn";
            }
            return firstRoundOnlyTakesCards(gameState) ?? needsMilitaryActions(player, 2);
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            setTacticAside(gameState, player);
            player.currentTactic = move.tacticName;
            player.currentTacticId = CopiedTactic(gameState, move).id ?? null;
            player.changedTacticThisTurn = true;
            player.militaryActionsUsed += 2;
        },
        describe: (gameState, move) => `Copied the ${move.tacticName} tactic\nUsed 2 military actions`,
    },

    [MoveTypes.DiscoverTechnology]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            if (card === undefined)
            {
                return "That card is not in your hand";
            }
            if (card.type !== CivilCardTypes.Technology)
            {
                return "That card is not a technology";
            }
            const tech = card.Technology;
            if (tech.type === TechnologyTypes.Government)
            {
                return "A government is changed peacefully or by a revolution";
            }
            if (player.HasTechnology(tech.name))
            {
                return "You already have this technology";
            }
            const cost = GetDevelopCost(player, tech, MoveChoice(move)).pay;
            if (player.science < cost)
            {
                return `Needs ${cost} science`;
            }
            const cooperation = require('./Pact').CooperationProblem(gameState, player);
            if (cooperation)
            {
                return cooperation;
            }
            return actionCheck(gameState, move, () => needsCivilActions(player, 1, { tech }));
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            const tech = card.Technology;
            const cost = GetDevelopCost(player, tech, MoveChoice(move));
            player.science -= cost.pay;
            PayCost(player, cost);
            require('./Pact').PayCooperation(gameState, player);
            player.civilHand = player.civilHand.filter(c => c !== card);
            // Of two special technologies of the same type only the higher level stays in play: usually the new one replaces
            // the old, but a lower one just developed is discarded (it still counts as developing a technology)
            const sameType = isSpecialType(tech.type) ? player.technologies.find(t => t.type === tech.type) : null;
            if (sameType && AgeOrder.indexOf(sameType.age) >= AgeOrder.indexOf(tech.age))
            {
                LogTrigger(player, tech.name, `discarded: ${sameType.name} is a higher level`);
            } else {
                if (sameType)
                {
                    player.technologies = player.technologies.filter(t => t.type !== tech.type);
                }
                player.AddTechnology(tech);
            }
            spendAction(player, move, false, { tech });
            player.turnFlags.push(TurnFlags.Developed);
            FireTriggers(player, TriggerEvents.Develop, { tech });
            require('./Aggression').KidnapBonus(player, card);
        },
        describe: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const card = player.civilHand.find(c => c?.id === move.cardId);
            return card ? `Discovered ${CardRef(card)}${spentLine(GetDevelopCost(player, card.Technology, MoveChoice(move)), 'science')}` : 'Discovered a technology';
        },
    },

    // { decisionId, value }: a player answers one of their decisions (see Decisions.js), whoever's turn it is.
    // Their oldest decision comes first.
    [MoveTypes.Decide]: {
        anyPlayer: true,
        validate: (gameState, move) => {
            const decision = gameState.decisions.find(d => d.id === move.decisionId);
            if (decision === undefined || decision.player !== move.player)
            {
                return "That decision is not yours to make";
            }
            if (NextDecisionFor(gameState, move.player).id !== decision.id)
            {
                return "Make your earlier decision first";
            }
            // Blocking decisions go one at a time, in turn order
            const order = DecisionOrderProblem(gameState, decision);
            if (order)
            {
                return order;
            }
            if (decision.options?.length > 0 && !decision.options.some(option => SameValue(option.value, move.value)))
            {
                return "Choose one of the options";
            }
            // Going back to an earlier choice is always allowed while it is offered
            if (move.value === Back && decision.back)
            {
                return null;
            }
            // Answers that depend on the board are checked now (it may have changed since the decision was asked)
            return DecisionTypes[decision.type].validate?.(gameState, decision, move.value) ?? null;
        },
        apply: (gameState, move) => {
            const decision = gameState.decisions.find(d => d.id === move.decisionId);
            const player = gameState.playerStates[move.player];
            // Anything it leaves owed is settled under the same heading (e.g. the aggression that took a colony)
            gameState.blueCause = decision.event;
            const logged = gameState.triggerLog.length;
            const text = ResolveDecision(gameState, decision, move.value);
            if (DecisionTypes[decision.type].ownEntry)
            {
                // (the entry as it is, ahead of what cards did in reaction while it was applied)
                gameState.triggerLog.splice(logged, 0, text);
            } else {
                LogTrigger(player, decision.event, `${player.name} ${text}`);
            }
        },
        describe: (gameState, move) => {
            const decision = gameState.decisions.find(d => d.id === move.decisionId);
            const name = gameState.playerStates[move.player].name;
            // A listed answer is named; a checked one is described by what it does (see the trigger log), as is one whose
            // result says what was chosen (e.g. "Border Conflict: Matt destroys Bronze") or is a journal entry of its own
            const type = DecisionTypes[decision?.type];
            if (type?.tellsItself || type?.ownEntry)
            {
                return '';
            }
            const option = decision?.options?.find(o => SameValue(o.value, move.value));
            const subject = decision ? DecisionSubject(gameState, decision) : undefined;
            return option ? `${name} chose "${option.label}" for ${subject}` : `${name} decided on ${subject}`;
        },
    },

    // { cardIds }: at the end of the turn, discards the military cards above the hand limit (face down)
    [MoveTypes.DiscardMilitaryCards]: {
        phase: Phase.Action,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const excess = MilitaryCardsOverLimit(player);
            if (excess === 0)
            {
                return "You are within your military hand limit";
            }
            const ids = move.cardIds ?? [];
            if (new Set(ids).size !== ids.length || !ids.every(id => player.militaryHand.some(card => card.id === id)))
            {
                return "Those cards are not in your military hand";
            }
            return ids.length === excess ? null : `Discard exactly ${excess} military card${excess === 1 ? "" : "s"}`;
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const discarded = player.militaryHand.filter(card => move.cardIds.includes(card.id));
            player.militaryHand = player.militaryHand.filter(card => !move.cardIds.includes(card.id));
            gameState.militaryDiscard.push(...discarded);
            FireTriggers(player, TriggerEvents.DiscardMilitary, { count: discarded.length });
        },
        // Discards are face down, so the cards are not named
        describe: (gameState, move) => `Discarded ${move.cardIds.length} military card${move.cardIds.length === 1 ? "" : "s"}`,
    },

    // { saladin? }: with Saladin in play, his choice for until the next one ('strength' or 'civil'), made right after
    // the action phase
    [MoveTypes.EndTurn]: {
        phase: Phase.Action,
        // Draws military cards and refills the card row, both of which reveal hidden cards
        irreversible: true,
        validate: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            const excess = MilitaryCardsOverLimit(player);
            if (excess > 0)
            {
                return `Discard ${excess} military card${excess === 1 ? "" : "s"} first`;
            }
            return player.leader?.name === LeaderNames.Saladin && !SaladinChoices[move.saladin] ? "Choose Saladin's bonus: +2 strength or +1 civil action" : null;
        },
        apply: (gameState, move) => {
            const player = gameState.playerStates[move.player];
            // (blue tokens other players owe from this turn are settled once it is over: see Rules.Apply)
            gameState.deferredBlue = [];
            if (player.leader?.name === LeaderNames.Saladin)
            {
                player.CardState(LeaderNames.Saladin).choice = move.saladin;
                LogTrigger(player, LeaderNames.Saladin, `${SaladinChoices[move.saladin]} until the next choice`);
            }

            // Skipped when there is an uprising
            FireTriggers(player, TriggerEvents.EndOfTurn);
            NobelPrize(gameState, player);
            RunProductionPhase(player);

            // Draw military cards for unused military actions
            const drawCount = Math.min(Math.max(player.GetMilitaryActionsRemaining(), 0), MAX_MILITARY_DRAW);
            gameState.DrawMilitaryCards(player, drawCount);

            player.civilActionsUsed = 0;
            player.militaryActionsUsed = 0;
            player.changedTacticThisTurn = false;
            player.turnFlags = [];
            player.turnPools = [];
            player.turnPoolUsage = {};
            player.turnBonuses = [];
            player.takenThisTurn = [];

            // The last turn of the game: then the final scoring
            const { IsLastTurn, LastActive, EndGame } = require('./GameEnd');
            if (IsLastTurn(gameState, player.playerNumber) && player.playerNumber === LastActive(gameState))
            {
                EndGame(gameState);
                return;
            }
            StartNextTurn(gameState, player.playerNumber);
        },
        describe: () => "Ended turn",
        // A line each, e.g. "Scored 3 culture (now 7)", "Scored 4 science (now 6)", "Food now 2, resources now 5",
        // "Drew 2 military cards"
        summarize: (before, after, move) => {
            const was = before.playerStates[move.player];
            const now = after.playerStates[move.player];
            const drew = `Drew ${now.militaryHand.length - was.militaryHand.length} military cards`;
            // The last turn: then the final scoring and the result
            const end = after.finished
                ? [`Final scoring: ${after.finished.finalScoring.map(r => `${r.name}: ${r.text}`).join('; ') || 'nothing'}`,
                    `Game over: ${after.finished.standings.map(s => `${s.name} ${s.culture}`).join(', ')}; ${after.finished.winners.join(' and ')} win${after.finished.winners.length === 1 ? 's' : ''}`]
                : [];
            if (was.HasUprising())
            {
                return [`Uprising: production skipped (${was.GetDiscontentWorkers()} discontent workers, ${was.workerPool} unused)`,
                    drew, ...end].join('\n');
            }
            return [`Scored ${now.culture - was.culture} culture (now ${now.culture})`,
                `Scored ${now.science - was.science} science (now ${now.science})`,
                `Food now ${now.GetFood()}, resources now ${now.GetResources()}`,
                drew, ...end].join('\n');
        },
    },

    // A player leaves the game, as their political action (see GameEnd.js). It cannot be undone.
    [MoveTypes.Resign]: {
        phase: Phase.Political,
        irreversible: true,
        validate: (gameState, move) => require('./GameEnd').ResignProblem(gameState, gameState.playerStates[move.player]),
        apply: (gameState, move) => require('./GameEnd').Resign(gameState, gameState.playerStates[move.player]),
        describe: () => "Resigned",
    },

    // A player taken out of the game from outside it (their account deleted), at any point (see GameEnd.js). Made only
    // by the server.
    [MoveTypes.Withdraw]: {
        anyPlayer: true,
        irreversible: true,
        validate: () => null,
        apply: (gameState, move) => require('./GameEnd').Withdraw(gameState, gameState.playerStates[move.player]),
        describe: () => "Left the game",
    },
}

// The next player's turn begins (players who resigned are skipped): their political phase (unless they gave it up),
// the tactic they played last turn becoming everyone's, the war they declared resolved, and the card row refilled
const StartNextTurn = (gameState, from) => {
    const { NextActive } = require('./GameEnd');
    const { next, newRound } = NextActive(gameState, from);
    gameState.currentPlayer = next;
    if (newRound)
    {
        gameState.round++;
        // The last round, known from an earlier round: announced as it begins
        if (gameState.finalRound === gameState.round)
        {
            gameState.reports.push({ kind: 'notice', name: 'Last round', round: gameState.round, changes: [],
                text: "The last round begins: this is everyone's last turn." });
        }
    }
    gameState.phase = gameState.round === 1 ? Phase.Action : Phase.Political;
    // The Olympic Games end as the turn of the player who declared them begins
    if (gameState.olympics?.player === next)
    {
        gameState.olympics = null;
    }
    // A player who gave up their next political action (International Agreement) goes straight to actions, unless they
    // can still do what comes before the political action (Coubertin's Olympic Games): then the political phase offers
    // only that, and passing it (the political actions are refused, see Rules.Validate)
    const player = gameState.GetCurrentPlayerState();
    if (player.skipNextPolitical && gameState.phase === Phase.Political)
    {
        player.skipNextPolitical = false;
        // (what does not need the political action still can be done: Coubertin's Olympic Games, Fleming's look)
        player.turnFlags.push(TurnFlags.PoliticalGivenUp);
        const before = HasPrePoliticalAction(player);
        if (!before)
        {
            gameState.phase = Phase.Action;
        }
        gameState.reports.push({ kind: 'notice', name: 'International Agreement', round: gameState.round, changes: [], journal: player.name,
            text: before ? `${player.name}'s political action is given up (International Agreement).`
                : `${player.name}'s political phase is skipped (given up for International Agreement).` });
    }
    // The start-of-turn sequence, in the rules' order: replenish the card row (not in the first round), resolve the war
    // the player declared, make their exclusive tactic available to everyone (a step Sun Tzu skips: it stays theirs);
    // then what cards do at the start of the turn (e.g. Hippocrates)
    if (gameState.round > 1)
    {
        refillCardRow(gameState);
    }
    require('./War').ResolveWars(gameState, player);
    if (!HasRuleException(player, Rules.KeepTacticPrivate))
    {
        makeTacticPublic(gameState, player);
    }
    FireTriggers(player, TriggerEvents.TurnStart);
    // Straight to the action phase (the first round, or the political phase given up): it starts now
    if (gameState.phase === Phase.Action)
    {
        StartActionPhase(gameState, player);
    }
};

// Whether the player can do something in the political phase before (and apart from) the political action: Coubertin's
// Olympic Games, not yet declared
const HasPrePoliticalAction = (player) =>
    player.leader?.name === LeaderNames.PierreDeCoubertin && !player.CardState(LeaderNames.PierreDeCoubertin).used;

// A political action has been played: the political phase ends, unless Julius Caesar lets the player play another
// (the player may also end the phase without it). The second one ends the phase; in the base game it uses him up (once
// per game), in the expansion's version he can do it every turn.
const EndPoliticalAction = (gameState, playerNumber) => {
    const player = gameState.playerStates[playerNumber];
    if (player.turnFlags.includes(TurnFlags.CaesarSecond))
    {
        player.turnFlags = player.turnFlags.filter(flag => flag !== TurnFlags.CaesarSecond);
        const oncePerGame = LeaderDefinition(player.leader)?.oncePerGame;
        if (oncePerGame)
        {
            player.CardState(LeaderNames.JuliusCaesar).used = true;
        }
        LogTrigger(player, LeaderNames.JuliusCaesar, oncePerGame ? "second political action (used for this game)" : "second political action");
        gameState.phase = Phase.Action;
        gameState.politicalResolving = true;
        StartActionPhase(gameState, player);
        return;
    }
    const caesar = player.leader?.name === LeaderNames.JuliusCaesar ? LeaderDefinition(player.leader) : null;
    if (caesar && !(caesar.oncePerGame && player.CardState(LeaderNames.JuliusCaesar).used))
    {
        player.turnFlags.push(TurnFlags.CaesarSecond);
        LogTrigger(player, LeaderNames.JuliusCaesar, caesar.oncePerGame ? "you may play another political action (once per game)" : "you may play another political action");
        return;
    }
    gameState.phase = Phase.Action;
    // Shown as the political phase until what it asked is decided (see Rules.Apply)
    gameState.politicalResolving = true;
    StartActionPhase(gameState, player);
};

// The common tactic a copy move names: by id when given, else the first of that name that has gained no strength
const CopiedTactic = (gameState, move) => move.tacticCardId !== undefined
    ? gameState.publicTactics.find(card => card.id === move.tacticCardId && card.name === move.tacticName)
    : gameState.publicTactics.find(card => card.name === move.tacticName && !gameState.tacticBonuses[card.id])
        ?? gameState.publicTactics.find(card => card.name === move.tacticName);

// Between turns: the first cards leave the row (as many as the player count says), the rest slide left, and new cards
// fill the gaps
const refillCardRow = (gameState) => fillCardRow(gameState, GetCardRowRemovalCount(require('./GameEnd').ActivePlayers(gameState).length));

// Replenishing without removing anything (International Agreement): the cards slide left and new cards fill the gaps
const ReplenishCardRow = (gameState) => fillCardRow(gameState, 0);

const fillCardRow = (gameState, removeCount) => {
    const remaining = gameState.civilCardRow.slice(removeCount).filter(card => card !== null);
    while (remaining.length < CARD_ROW_SIZE)
    {
        // (a deck already empty: an age that has not begun yet, in a game saved that way; or nothing left in Age IV)
        if (gameState.civilDeck.length === 0 && !gameState.StartNextAge())
        {
            break;
        }
        remaining.push(gameState.civilDeck.shift());
        // An age ends as soon as its civil deck is dealt out, not when a card is next needed: the next age begins
        // at once (Age IV with Age III's last card), and its deck continues the refill
        if (gameState.civilDeck.length === 0)
        {
            gameState.StartNextAge();
        }
    }
    while (remaining.length < CARD_ROW_SIZE)
    {
        remaining.push(null);
    }
    gameState.civilCardRow = remaining;
    // Age A ends at the first refill (the starting player's second turn): the rest of the Age A deck is
    // removed from the game and the Age I civil and military decks come into play
    if (gameState.age === Age.AgeA)
    {
        gameState.StartNextAge();
    }
}

// Barbarossa's population (food) and unit (resources) costs, each 1 less
function barbarossaCosts(player, tech, declinePools)
{
    const source = LeaderNames.FrederickBarbarossa;
    const population = WithCardDiscount(player, { source, kind: CostKinds.PopulationFood, amount: 1 }, () => GetPopulationCost(player));
    const unit = WithCardDiscount(player, { source, kind: CostKinds.BuildPayment, amount: 1 }, () => GetBuildCost(player, tech, declinePools));
    return { population, unit };
}

// Whether an action card is played to declare a revolution (Breakthrough): its cost (all civil actions, or with
// Robespierre all military actions) is paid instead of the card's civil action
function RevolutionWithCard(move)
{
    return move.inner?.type === MoveTypes.ChangeGovernment && !!move.inner.revolution;
}

// The action that comes with an action card, as a move of its own that spends no action
function innerMove(move)
{
    return { ...move.inner, player: move.player, free: true, declinePools: MoveChoice(move) };
}

// The discount an action card gives the action that comes with it
function cardDiscount(card, effect, player)
{
    const discount = effect.discount?.(card, player);
    return discount ? { source: card.name, kind: discount.kind, amount: discount.amount } : null;
}

// Why the action given with an action card cannot come with it, or null
function innerActionError(gameState, player, card, effect, inner, declinePools)
{
    if (!inner || !effect.inner.moves.includes(inner.type))
    {
        return `Choose what to do with ${card.name}`;
    }
    if (effect.inner.techTypes)
    {
        const tech = player.GetTechnology(inner.techName ?? inner.fromTech);
        if (!tech || !effect.inner.techTypes.includes(tech.type))
        {
            return `${card.name} cannot be used on that card`;
        }
    }
    if (effect.inner.stages !== undefined && inner.stages !== effect.inner.stages)
    {
        return `${card.name} builds ${effect.inner.stages} stage`;
    }
    const move = innerMove({ inner, player: player.playerNumber, declinePools });
    return WithCardDiscount(player, cardDiscount(card, effect, player), () => MoveDefinitions[inner.type].validate(gameState, move));
}

module.exports = MoveDefinitions;
// Not a move (kept off the move table so it does not show up as one)
Object.defineProperty(MoveDefinitions, 'ReplenishCardRow', { value: ReplenishCardRow, enumerable: false });
Object.defineProperty(MoveDefinitions, 'StartNextTurn', { value: StartNextTurn, enumerable: false });
Object.defineProperty(MoveDefinitions, 'NobelPrize', { value: NobelPrize, enumerable: false });
Object.defineProperty(MoveDefinitions, 'CompleteWonder', { value: CompleteWonder, enumerable: false });